
import { defineComponent, ref, onMounted } from 'vue'
import { createCoupons, listCoupons, toggleCoupon, deleteCoupon, CouponCode } from '@/services/admin'

export default defineComponent({
  name: 'CouponManagement',
  setup() {
    const coupons = ref<CouponCode[]>([])
    const page = ref(1)
    const totalPages = ref(1)
    const searchQuery = ref('')
    const statusFilter = ref('')
    const showCreateDialog = ref(false)
    const creating = ref(false)
    const createError = ref('')
    const createdCoupons = ref<CouponCode[]>([])
    const form = ref({ amount: 10, expiresAt: '', maxUses: 1, remark: '', count: 1 })

    let debounceTimer: ReturnType<typeof setTimeout> | null = null

    const loadCoupons = async (p: number) => {
      page.value = p
      const result = await listCoupons(p, 20, searchQuery.value, statusFilter.value)
      coupons.value = result.list
      totalPages.value = Math.ceil(result.total / 20)
    }

    const debouncedSearch = () => {
      if (debounceTimer) clearTimeout(debounceTimer)
      debounceTimer = setTimeout(() => loadCoupons(1), 300)
    }

    const doCreate = async () => {
      creating.value = true
      createError.value = ''
      createdCoupons.value = []
      try {
        const result = await createCoupons({
          amount: form.value.amount,
          expiresAt: form.value.expiresAt ? new Date(form.value.expiresAt).toISOString() : undefined,
          maxUses: form.value.maxUses,
          remark: form.value.remark,
          count: form.value.count,
        })
        createdCoupons.value = result.coupons
        await loadCoupons(1)
      }
      catch (e) {
        createError.value = e instanceof Error ? e.message : '创建失败'
      }
      finally {
        creating.value = false
      }
    }

    const closeCreateDialog = () => {
      showCreateDialog.value = false
      createdCoupons.value = []
      createError.value = ''
      form.value = { amount: 10, expiresAt: '', maxUses: 1, remark: '', count: 1 }
    }

    const copyText = async (text: string) => {
      await navigator.clipboard.writeText(text)
    }

    const copyAllCodes = () => {
      const all = createdCoupons.value.map(c => c.code).join('\n')
      copyText(all)
    }

    const copyLink = (code: string) => {
      const url = `${window.location.origin}/?code=${code}`
      copyText(url)
    }

    const doToggle = async (c: CouponCode) => {
      await toggleCoupon(c.id)
      await loadCoupons(page.value)
    }

    const confirmDeleteId = ref('')

    const doDelete = async (id: string) => {
      if (confirmDeleteId.value !== id) {
        confirmDeleteId.value = id
        setTimeout(() => {
          if (confirmDeleteId.value === id) confirmDeleteId.value = ''
        }, 3000)
        return
      }
      confirmDeleteId.value = ''
      await deleteCoupon(id)
      await loadCoupons(page.value)
    }

    onMounted(() => loadCoupons(1))

    return {
      coupons, page, totalPages, searchQuery, statusFilter,
      showCreateDialog, creating, createError, createdCoupons, form,
      loadCoupons, debouncedSearch, doCreate, closeCreateDialog, copyText, copyAllCodes,
      copyLink, confirmDeleteId, doToggle, doDelete,
    }
  },
})
